import type * as MonacoReactEditorModule from "@monaco-editor/react";
import type * as MonacoEditorModule from "monaco-editor";
import type { editor } from "monaco-editor";
import { useEffect } from "react";
import { create } from "zustand";

import { StudioSupportedLangsSchema } from "@acme/validators";

import { githubDarkTheme, githubLightTheme } from "./_utils/monaco-themes";
import { useStudioStore } from "./use-studio-store";

const DTS_PATHS = Object.fromEntries(
  Object.values(StudioSupportedLangsSchema.Values).map((lang) => {
    return [
      `file:///node_modules/ast-grep-wasm/types/${lang}.d.ts`,
      {
        filePath: `/parsers/ts-types/${lang}.d.ts.txt`,
        moduleName: `@ast-grep/napi/lang/${lang}`,
      },
    ];
  }),
);
DTS_PATHS["file:///node_modules/@ast-grep/napi/index.d.ts"] = {
  filePath: "/data/ast_grep_wasm.d.ts.txt",
  moduleName: "@ast-grep/napi",
};

let monacoReactModule: typeof MonacoReactEditorModule | null = null;
async function loadMonacoModule() {
  monacoReactModule ??= (await import("./_utils/monaco-loader"))
    .monacoEditorModule;
  return monacoReactModule;
}

type EditorId = string;

interface MonacoEditorState {
  // Editor modules
  monacoReactModule: typeof MonacoReactEditorModule | null;
  monacoEditorModule: typeof MonacoEditorModule | null;
  setMonacoReactModule: (
    monacoReactModule: typeof MonacoReactEditorModule | null,
  ) => void;
  setMonacoEditorModule: (
    monacoEditorModule: typeof MonacoEditorModule | null,
  ) => void;

  // Editor instances
  editorInstances: Map<EditorId, editor.IStandaloneCodeEditor>;
  setEditorInstance: (
    id: EditorId,
    editor: editor.IStandaloneCodeEditor,
  ) => void;
  removeEditorInstance: (id: EditorId) => void;
  clearEditorInstances: () => void;
}

const useMonacoEditorStore = create<MonacoEditorState>((set, get) => ({
  monacoReactModule: null,
  monacoEditorModule: null,
  editorInstances: new Map(),
  setMonacoReactModule: (monacoReactModule) => set({ monacoReactModule }),
  setMonacoEditorModule: (monacoEditorModule) => set({ monacoEditorModule }),
  setEditorInstance: (id, editor) =>
    set({ editorInstances: new Map(get().editorInstances).set(id, editor) }),
  removeEditorInstance: (id) => {
    const editorInstances = new Map(get().editorInstances);
    editorInstances.delete(id);
    set({ editorInstances });
  },
  clearEditorInstances: () => set({ editorInstances: new Map() }),
}));

export function useInitializeMonacoEditor() {
  const setMonacoReactModule = useMonacoEditorStore(
    (state) => state.setMonacoReactModule,
  );
  const setMonacoEditorModule = useMonacoEditorStore(
    (state) => state.setMonacoEditorModule,
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    void loadMonacoModule().then(async (module) => {
      await module.loader.init().then(async (editorModule) => {
        setMonacoReactModule(module);
        setMonacoEditorModule(editorModule);

        await Promise.all(
          Object.entries(DTS_PATHS).map(async ([uri, { filePath }]) => {
            editorModule.editor.defineTheme("github-dark", githubDarkTheme);
            editorModule.editor.defineTheme("github-light", githubLightTheme);

            try {
              // Fetch the type definitions file
              const response = await fetch(filePath);
              if (response.ok) {
                const astGrepWasmTypes = await response.text();

                // Add type definitions for ast-grep-wasm
                editorModule.languages.typescript.typescriptDefaults.addExtraLib(
                  astGrepWasmTypes,
                  uri,
                );
              } else {
                console.error("Failed to load ast-grep-wasm type definitions");
              }
            } catch (error) {
              console.error(
                "Error loading ast-grep-wasm type definitions:",
                error,
              );
            }
          }),
        );

        const compilerOptions =
          editorModule.languages.typescript.typescriptDefaults.getCompilerOptions();
        editorModule.languages.typescript.typescriptDefaults.setCompilerOptions(
          {
            ...compilerOptions,
            moduleResolution:
              editorModule.languages.typescript.ModuleResolutionKind.NodeJs,
            allowSyntheticDefaultImports: true,
            paths: {
              ...Object.entries(DTS_PATHS).reduce(
                (acc, [uri, { moduleName }]) => {
                  return {
                    ...acc,
                    [moduleName]: [uri],
                  };
                },
                {} as Record<string, string[]>,
              ),
            },
          },
        );
      });
    });
  }, [setMonacoEditorModule, setMonacoReactModule]);
}

export function useMonacoEditor() {
  const monacoReactModule = useMonacoEditorStore(
    (state) => state.monacoReactModule,
  );
  const monacoEditorModule = useMonacoEditorStore(
    (state) => state.monacoEditorModule,
  );

  return {
    monacoReactModule,
    monacoEditorModule,
  };
}

export function useActiveEditorInstance() {
  const editorInstances = useMonacoEditorStore(
    (state) => state.editorInstances,
  );
  const activeEditorId = useActiveEditorId();
  return editorInstances.get(activeEditorId) ?? null;
}

export function useEditorInstanceUpdaters() {
  const setEditorInstance = useMonacoEditorStore(
    (state) => state.setEditorInstance,
  );
  const clearEditorInstances = useMonacoEditorStore(
    (state) => state.clearEditorInstances,
  );
  const removeEditorInstance = useMonacoEditorStore(
    (state) => state.removeEditorInstance,
  );
  return { setEditorInstance, clearEditorInstances, removeEditorInstance };
}

export function useActiveEditorId() {
  const activeRemoteRunFileId = useStudioStore(
    (state) => state.activeRemoteRunFileId,
  );
  const activeTestSourceId = useStudioStore(
    (state) => state.activeTestSourceId,
  );

  const activeEditorId = activeRemoteRunFileId
    ? makeEditorId(activeRemoteRunFileId, "remote_run")
    : makeEditorId(activeTestSourceId, "source");

  return activeEditorId;
}

export function makeEditorId(
  id: string | number,
  type: "source" | "remote_run",
) {
  return `${type}-${id}`;
}

export function isIdRemoteRun(id: string | number) {
  return id.toString().startsWith("remote_run-");
}

export function getRealIdFromTabId(tabId: string) {
  return tabId.replace(/^(remote_run|source)-/, "");
}

const AREA_REGEX = /(.+)@(\d+):(\d+)-?(\d+):(\d+)$/;

export interface RemoteRunFileArea {
  startLine: number;
  startColumn: number;
  endLine: number;
  endColumn: number;
}

export function parseRemoteRunFileId(id: string) {
  const match = AREA_REGEX.exec(id);
  if (!match) {
    return { filePath: id, area: null };
  }
  const [, filePath, startLine, startColumn, endLine, endColumn] = match;

  return {
    filePath: filePath ?? "",
    area: {
      startLine: parseInt(startLine ?? "0"),
      startColumn: parseInt(startColumn ?? "0"),
      endLine: parseInt(endLine ?? "0"),
      endColumn: parseInt(endColumn ?? "0"),
    } satisfies RemoteRunFileArea,
  };
}
